import { useState, useEffect, useMemo, ReactNode, useRef } from "react";
import { useLocation } from "react-router-dom";
import { DatabaseContext, DatabaseContextType } from "./DatabaseContext";
import { getDBPool, type DBPool } from "../duckdb";
import { useAuth } from "../auth/AuthContext";

const INIT_MESSAGES = [
  "Setting up the warehouse…",
  "Preparing your data…",
  "Initializing engine…",
  "Spinning up DuckDB…",
  "Loading parquet data…",
  "Almost ready…",
];

interface DatabaseProviderProps {
  children: ReactNode;
}

const DUCKDB_ROUTES = ["/"];

export function DatabaseProvider({ children }: DatabaseProviderProps) {
  const { isLoading: authLoading, isAuthenticated, requiresAuth } = useAuth();
  const { pathname } = useLocation();
  const showOverlay = DUCKDB_ROUTES.includes(pathname);
  const [dbPool, setDbPool] = useState<DBPool | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (requiresAuth && !isAuthenticated) {
      setDbPool(null);
      setIsLoading(false);
      return;
    }

    // Prevent multiple initializations
    if (hasInitialized.current) {
      return;
    }

    const init = async () => {
      try {
        hasInitialized.current = true;
        setIsLoading(true);
        setError(null);
        const startTime = performance.now();
        console.log(`[DatabaseProvider] Starting database initialization...`);
        const pool: DBPool = await getDBPool(requiresAuth);
        const endTime = performance.now();
        const duration = endTime - startTime;
        console.log(`[DatabaseProvider] Database initialized in ${duration.toFixed(2)} ms`);
        setDbPool(pool);
      } catch (err) {
        console.error("Failed to initialize database:", err);
        setError(err instanceof Error ? err.message : "Database initialization failed");
        // Reset the flag on error so retry is possible if needed
        hasInitialized.current = false;
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [isAuthenticated, requiresAuth, authLoading]);

  const contextValue: DatabaseContextType = {
    dbPool,
    isLoading,
    error,
  };

  const initMessage = useMemo(() => INIT_MESSAGES[Math.floor(Math.random() * INIT_MESSAGES.length)], []);

  return (
    <DatabaseContext.Provider value={contextValue}>
      {children}
      {showOverlay && isLoading && <InitToast message={initMessage} />}
      {showOverlay && error && (
        <div className="init-toast init-toast--error">
          <span className="init-toast__text">Failed to load database</span>
          <span className="init-toast__detail">{error}</span>
        </div>
      )}
    </DatabaseContext.Provider>
  );
}

function InitToast({ message }: { message: string }) {
  return (
    <div className="init-toast">
      <div className="init-toast__bar" />
      <span className="init-toast__text">{message}</span>
    </div>
  );
}
